import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Autocomplete,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  InputAdornment,
  Card,
  CardContent
} from '@mui/material';
import {
  PersonAdd as AssignIcon,
  SwapHoriz as TransferIcon,
  Search as SearchIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import {
  getPaginatedUsers,
  searchUsers,
  bulkAssignClientsToTherapist,
  transferClients
} from '../../../services/api';

function ClientAssignmentsTab({ token, stats, onRefresh }) {
  // Bulk Assign State
  const [unassignedClients, setUnassignedClients] = useState([]);
  const [selectedForAssign, setSelectedForAssign] = useState([]);
  const [assignToTherapist, setAssignToTherapist] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState('');

  // Bulk Transfer State
  const [assignedClients, setAssignedClients] = useState([]);
  const [selectedForTransfer, setSelectedForTransfer] = useState([]);
  const [transferFromTherapist, setTransferFromTherapist] = useState(null);
  const [transferToTherapist, setTransferToTherapist] = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState('');

  // Therapist Search (for assign section)
  const [assignTherapistSearchQuery, setAssignTherapistSearchQuery] = useState('');
  const [assignTherapistSearchResults, setAssignTherapistSearchResults] = useState([]);
  const [assignTherapistSearchLoading, setAssignTherapistSearchLoading] = useState(false);

  // Therapist Search (for transfer section)
  const [transferTherapistSearchQuery, setTransferTherapistSearchQuery] = useState('');
  const [transferTherapistSearchResults, setTransferTherapistSearchResults] = useState([]);
  const [transferTherapistSearchLoading, setTransferTherapistSearchLoading] = useState(false);

  // Pagination for unassigned
  const [unassignedPage, setUnassignedPage] = useState(0);
  const [unassignedRowsPerPage, setUnassignedRowsPerPage] = useState(25);
  const [unassignedTotalCount, setUnassignedTotalCount] = useState(0);
  const [unassignedSearchQuery, setUnassignedSearchQuery] = useState('');

  // Pagination for assigned (filtered by therapist)
  const [assignedPage, setAssignedPage] = useState(0);
  const [assignedRowsPerPage, setAssignedRowsPerPage] = useState(25);
  const [assignedTotalCount, setAssignedTotalCount] = useState(0);
  const [assignedSearchQuery, setAssignedSearchQuery] = useState('');

  // Load unassigned clients
  useEffect(() => {
    loadUnassignedClients();
  }, [unassignedPage, unassignedRowsPerPage, unassignedSearchQuery, token]);

  // Load assigned clients when therapist is selected
  useEffect(() => {
    if (transferFromTherapist) {
      loadAssignedClients();
    } else {
      setAssignedClients([]);
      setSelectedForTransfer([]);
    }
  }, [transferFromTherapist, assignedPage, assignedRowsPerPage, assignedSearchQuery, token]);

  // Load initial therapists for assign section on mount
  useEffect(() => {
    loadInitialTherapistsForAssign();
  }, [token]);

  // Load initial therapists for transfer section on mount
  useEffect(() => {
    loadInitialTherapistsForTransfer();
  }, [token]);

  // Search therapists for assign section
  useEffect(() => {
    if (assignTherapistSearchQuery.length >= 2) {
      const timer = setTimeout(async () => {
        try {
          setAssignTherapistSearchLoading(true);
          const data = await searchUsers({
            q: assignTherapistSearchQuery,
            role: 'therapist',
            limit: 20
          }, token);
          setAssignTherapistSearchResults(data.users || []);
        } catch (err) {
          console.error('Error searching therapists:', err);
        } finally {
          setAssignTherapistSearchLoading(false);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else if (assignTherapistSearchQuery.length === 0) {
      loadInitialTherapistsForAssign();
    }
  }, [assignTherapistSearchQuery, token]);

  // Search therapists for transfer section
  useEffect(() => {
    if (transferTherapistSearchQuery.length >= 2) {
      const timer = setTimeout(async () => {
        try {
          setTransferTherapistSearchLoading(true);
          const data = await searchUsers({
            q: transferTherapistSearchQuery,
            role: 'therapist',
            limit: 20
          }, token);
          setTransferTherapistSearchResults(data.users || []);
        } catch (err) {
          console.error('Error searching therapists:', err);
        } finally {
          setTransferTherapistSearchLoading(false);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else if (transferTherapistSearchQuery.length === 0) {
      loadInitialTherapistsForTransfer();
    }
  }, [transferTherapistSearchQuery, token]);

  const loadInitialTherapistsForAssign = async () => {
    try {
      setAssignTherapistSearchLoading(true);
      const data = await getPaginatedUsers({
        page: 1,
        limit: 50,
        role: 'therapist',
        status: 'Active',
        sortBy: 'name',
        sortOrder: 'asc'
      }, token);
      setAssignTherapistSearchResults(data.users || []);
    } catch (err) {
      console.error('Error loading therapists:', err);
    } finally {
      setAssignTherapistSearchLoading(false);
    }
  };

  const loadInitialTherapistsForTransfer = async () => {
    try {
      setTransferTherapistSearchLoading(true);
      const data = await getPaginatedUsers({
        page: 1,
        limit: 50,
        role: 'therapist',
        status: 'Active',
        sortBy: 'name',
        sortOrder: 'asc'
      }, token);
      setTransferTherapistSearchResults(data.users || []);
    } catch (err) {
      console.error('Error loading therapists:', err);
    } finally {
      setTransferTherapistSearchLoading(false);
    }
  };

  const loadUnassignedClients = async () => {
    try {
      const params = {
        page: unassignedPage + 1,
        limit: unassignedRowsPerPage,
        role: 'client',
        sortBy: 'name',
        sortOrder: 'asc',
        unassignedOnly: 'true' // Backend filter for unassigned clients
      };

      if (unassignedSearchQuery) {
        params.search = unassignedSearchQuery;
      }

      const data = await getPaginatedUsers(params, token);
      setUnassignedClients(data.users || []);
      setUnassignedTotalCount(data.pagination?.total || 0);
    } catch (err) {
      console.error('Error loading unassigned clients:', err);
      setAssignError(err.message || 'Failed to load clients');
    }
  };

  const loadAssignedClients = async () => {
    try {
      const params = {
        page: assignedPage + 1,
        limit: assignedRowsPerPage,
        role: 'client',
        sortBy: 'name',
        sortOrder: 'asc',
        assignedToTherapist: transferFromTherapist._id // Backend filter for specific therapist
      };

      if (assignedSearchQuery) {
        params.search = assignedSearchQuery;
      }

      const data = await getPaginatedUsers(params, token);
      setAssignedClients(data.users || []);
      setAssignedTotalCount(data.pagination?.total || 0);
    } catch (err) {
      console.error('Error loading assigned clients:', err);
      setTransferError(err.message || 'Failed to load clients');
    }
  };

  const handleBulkAssign = async () => {
    if (selectedForAssign.length === 0) {
      setAssignError('Please select at least one client');
      return;
    }
    if (!assignToTherapist) {
      setAssignError('Please select a therapist');
      return;
    }

    // Validate capacity
    const currentCount = assignToTherapist.therapistInfo?.assignedClientCount || 0;
    const maxCapacity = assignToTherapist.therapistInfo?.maxClientCapacity || 20;
    const availableSlots = maxCapacity - currentCount;

    if (selectedForAssign.length > availableSlots) {
      setAssignError(`Therapist only has ${availableSlots} available slots. Selected ${selectedForAssign.length} clients.`);
      return;
    }

    try {
      setAssignLoading(true);
      setAssignError('');
      await bulkAssignClientsToTherapist(selectedForAssign, assignToTherapist._id, token);

      // Reset and refresh
      setSelectedForAssign([]);
      setAssignToTherapist(null);
      await loadUnassignedClients();
      onRefresh();
    } catch (err) {
      console.error('Error assigning clients:', err);
      setAssignError(err.message || 'Failed to assign clients');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleBulkTransfer = async () => {
    if (selectedForTransfer.length === 0) {
      setTransferError('Please select at least one client');
      return;
    }
    if (!transferToTherapist) {
      setTransferError('Please select a destination therapist');
      return;
    }

    // Validate capacity
    const currentCount = transferToTherapist.therapistInfo?.assignedClientCount || 0;
    const maxCapacity = transferToTherapist.therapistInfo?.maxClientCapacity || 20;
    const availableSlots = maxCapacity - currentCount;

    if (selectedForTransfer.length > availableSlots) {
      setTransferError(`Destination therapist only has ${availableSlots} available slots. Selected ${selectedForTransfer.length} clients.`);
      return;
    }

    try {
      setTransferLoading(true);
      setTransferError('');
      await transferClients(
        selectedForTransfer,
        transferFromTherapist._id,
        transferToTherapist._id,
        token
      );

      // Reset and refresh
      setSelectedForTransfer([]);
      setTransferFromTherapist(null);
      setTransferToTherapist(null);
      await loadUnassignedClients();
      onRefresh();
    } catch (err) {
      console.error('Error transferring clients:', err);
      setTransferError(err.message || 'Failed to transfer clients');
    } finally {
      setTransferLoading(false);
    }
  };

  return (
    <Box>
      {/* Stats Summary */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="caption" color="text.secondary">Unassigned Clients</Typography>
            <Typography variant="h4" color="warning.main">
              {stats?.unassignedClients || 0}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="caption" color="text.secondary">Assigned Clients</Typography>
            <Typography variant="h4" color="success.main">
              {stats?.assignedClients || 0}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="caption" color="text.secondary">Available Therapists</Typography>
            <Typography variant="h4">
              {stats?.therapistsAvailable || 0}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="caption" color="text.secondary">Avg Clients/Therapist</Typography>
            <Typography variant="h4">
              {stats?.avgClientsPerTherapist?.toFixed(1) || '0.0'}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Bulk Assign Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AssignIcon color="primary" />
          <Typography variant="h6">Bulk Assign Unassigned Clients</Typography>
        </Box>

        {assignError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAssignError('')}>
            {assignError}
          </Alert>
        )}

        {/* Therapist Selection */}
        <Autocomplete
          value={assignToTherapist}
          onChange={(e, newValue) => setAssignToTherapist(newValue)}
          inputValue={assignTherapistSearchQuery}
          onInputChange={(e, newValue) => setAssignTherapistSearchQuery(newValue)}
          options={assignTherapistSearchResults}
          getOptionLabel={(option) => `${option.name} - ${option.therapistInfo?.assignedClientCount || 0}/${option.therapistInfo?.maxClientCapacity || 20} clients`}
          loading={assignTherapistSearchLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Therapist"
              placeholder="Start typing to search or select from list..."
              sx={{ mb: 2 }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {assignTherapistSearchLoading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option._id}>
              <Box>
                <Typography variant="body1">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.email} • {option.therapistInfo?.assignedClientCount || 0}/{option.therapistInfo?.maxClientCapacity || 20} clients
                </Typography>
              </Box>
            </li>
          )}
        />

        {/* Search unassigned */}
        <TextField
          fullWidth
          size="small"
          label="Search unassigned clients"
          value={unassignedSearchQuery}
          onChange={(e) => setUnassignedSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Unassigned Clients Table */}
        <TableContainer component={Paper} sx={{ maxHeight: 400, mb: 2 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedForAssign.length > 0 && selectedForAssign.length < unassignedClients.length}
                    checked={unassignedClients.length > 0 && selectedForAssign.length === unassignedClients.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedForAssign(unassignedClients.map(c => c._id));
                      } else {
                        setSelectedForAssign([]);
                      }
                    }}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {unassignedClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No unassigned clients</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                unassignedClients.map((client) => (
                  <TableRow
                    key={client._id}
                    hover
                    onClick={() => {
                      if (selectedForAssign.includes(client._id)) {
                        setSelectedForAssign(selectedForAssign.filter(id => id !== client._id));
                      } else {
                        setSelectedForAssign([...selectedForAssign, client._id]);
                      }
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedForAssign.includes(client._id)} />
                    </TableCell>
                    <TableCell>{client.name}</TableCell>
                    <TableCell>{client.email}</TableCell>
                    <TableCell>
                      <Chip label={client.status} size="small" color={client.status === 'Active' ? 'success' : 'default'} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={unassignedTotalCount}
          page={unassignedPage}
          onPageChange={(e, newPage) => setUnassignedPage(newPage)}
          rowsPerPage={unassignedRowsPerPage}
          onRowsPerPageChange={(e) => {
            setUnassignedRowsPerPage(parseInt(e.target.value, 10));
            setUnassignedPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Selected: {selectedForAssign.length}
          </Typography>
          <Button
            variant="contained"
            startIcon={assignLoading ? <CircularProgress size={20} /> : <AssignIcon />}
            onClick={handleBulkAssign}
            disabled={assignLoading || selectedForAssign.length === 0 || !assignToTherapist}
          >
            Assign {selectedForAssign.length} Client{selectedForAssign.length !== 1 ? 's' : ''}
          </Button>
        </Box>
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Bulk Transfer Section */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TransferIcon color="primary" />
          <Typography variant="h6">Bulk Transfer Clients Between Therapists</Typography>
        </Box>

        {transferError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTransferError('')}>
            {transferError}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
          {/* From Therapist */}
          <Box>
            <Autocomplete
              value={transferFromTherapist}
              onChange={(e, newValue) => {
                setTransferFromTherapist(newValue);
                setSelectedForTransfer([]);
              }}
              inputValue={transferTherapistSearchQuery}
              onInputChange={(e, newValue) => setTransferTherapistSearchQuery(newValue)}
              options={transferTherapistSearchResults}
              getOptionLabel={(option) => `${option.name} (${option.therapistInfo?.assignedClientCount || 0} clients)`}
              loading={transferTherapistSearchLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="From Therapist (Source)"
                  placeholder="Select therapist to transfer from..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {transferTherapistSearchLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option._id}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email} • {option.therapistInfo?.assignedClientCount || 0} clients
                    </Typography>
                  </Box>
                </li>
              )}
            />
            {transferFromTherapist && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                This therapist's clients will appear below for selection
              </Typography>
            )}
          </Box>

          {/* To Therapist */}
          <Box>
            <Autocomplete
              value={transferToTherapist}
              onChange={(e, newValue) => setTransferToTherapist(newValue)}
              inputValue={transferTherapistSearchQuery}
              onInputChange={(e, newValue) => setTransferTherapistSearchQuery(newValue)}
              options={transferTherapistSearchResults.filter(t => !transferFromTherapist || t._id !== transferFromTherapist._id)}
              getOptionLabel={(option) => `${option.name} - ${option.therapistInfo?.assignedClientCount || 0}/${option.therapistInfo?.maxClientCapacity || 20}`}
              loading={transferTherapistSearchLoading}
              disabled={!transferFromTherapist}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="To Therapist (Destination)"
                  placeholder="Select destination therapist..."
                  disabled={!transferFromTherapist}
                  helperText={!transferFromTherapist ? 'Select source therapist first' : ''}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {transferTherapistSearchLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option._id}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email} • {option.therapistInfo?.assignedClientCount || 0}/{option.therapistInfo?.maxClientCapacity || 20} clients • {option.therapistInfo?.maxClientCapacity - (option.therapistInfo?.assignedClientCount || 0)} slots available
                    </Typography>
                  </Box>
                </li>
              )}
            />
          </Box>
        </Box>

        {transferFromTherapist && (
          <>
            {/* Search assigned */}
            <TextField
              fullWidth
              size="small"
              label="Search clients"
              value={assignedSearchQuery}
              onChange={(e) => setAssignedSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Assigned Clients Table */}
            <TableContainer component={Paper} sx={{ maxHeight: 400, mb: 2 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedForTransfer.length > 0 && selectedForTransfer.length < assignedClients.length}
                        checked={assignedClients.length > 0 && selectedForTransfer.length === assignedClients.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedForTransfer(assignedClients.map(c => c._id));
                          } else {
                            setSelectedForTransfer([]);
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignedClients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No clients assigned to this therapist</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    assignedClients.map((client) => (
                      <TableRow
                        key={client._id}
                        hover
                        onClick={() => {
                          if (selectedForTransfer.includes(client._id)) {
                            setSelectedForTransfer(selectedForTransfer.filter(id => id !== client._id));
                          } else {
                            setSelectedForTransfer([...selectedForTransfer, client._id]);
                          }
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox checked={selectedForTransfer.includes(client._id)} />
                        </TableCell>
                        <TableCell>{client.name}</TableCell>
                        <TableCell>{client.email}</TableCell>
                        <TableCell>
                          <Chip label={client.status} size="small" color={client.status === 'Active' ? 'success' : 'default'} />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={assignedTotalCount}
              page={assignedPage}
              onPageChange={(e, newPage) => setAssignedPage(newPage)}
              rowsPerPage={assignedRowsPerPage}
              onRowsPerPageChange={(e) => {
                setAssignedRowsPerPage(parseInt(e.target.value, 10));
                setAssignedPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Selected: {selectedForTransfer.length}
              </Typography>
              <Button
                variant="contained"
                startIcon={transferLoading ? <CircularProgress size={20} /> : <TransferIcon />}
                onClick={handleBulkTransfer}
                disabled={transferLoading || selectedForTransfer.length === 0 || !transferToTherapist}
              >
                Transfer {selectedForTransfer.length} Client{selectedForTransfer.length !== 1 ? 's' : ''}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}

export default ClientAssignmentsTab;
